import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  Row,
  Col,
  Input,
} from "reactstrap";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import Select from "components/Select";
import { useUpdateReport } from "api/reports/service";
import moment from "moment";
import ReportScheduleFilterParameters from "./ReportScheduleFilterParameters";
import ReportScheduleReportSection from "./ReportScheduleReportSection";
import ReportScheduleRecipients from "./ReportScheduleRecipients";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  useReportScheduleTheme,
  getReportScheduleThemeCss,
  getReportSchedulePanelBoxStyle,
} from "./reportScheduleTheme";
import {
  parseScheduleFromApi,
  buildSchedulePayload,
  scheduleRanToday,
} from "./reportScheduleMapper";
import { downloadScheduleReportCsv } from "api/reports/api";
import ReportScheduleHistoryCalendar from "./ReportScheduleHistoryCalendar";

const ScheduleFieldBox = ({ label, children, fieldBoxStyle, bodyColor }) => (
  <div style={fieldBoxStyle}>
    <div className="d-flex align-items-center" style={{ minHeight: 38 }}>
      <div
        className="report-schedule-field-label"
        style={{
          width: "42%",
          flexShrink: 0,
          paddingRight: 12,
          fontSize: 14,
          fontWeight: 400,
          color: bodyColor,
        }}
      >
        {label}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  </div>
);

const ReportSchedule = forwardRef(
  (
    {
      activeColor = "blue",
      isTheme2 = false,
      id,
      permissions = [],
      columns = [],
      filters = [],
      onFiltersChange,
      initialScheduleData = null,
      lockFilters = false,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const updateReportMutation = useUpdateReport();
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const { colors, selectStyles, inputStyle, theme2FieldsCss, scopeClassName } = useReportScheduleTheme(
      isTheme2,
      activeColorStyle
    );

    const fieldBoxStyle = useMemo(
      () => ({
        backgroundColor: colors.fieldBg,
        border: `1px solid ${colors.fieldBorder}`,
        padding: "8px 12px",
        marginBottom: 8,
      }),
      [colors]
    );

    const exportPanelStyle = useMemo(
      () => getReportSchedulePanelBoxStyle(colors),
      [colors]
    );

    const [showPassword, setShowPassword] = useState(false);
    const [scheduleId, setScheduleId] = useState(null);
    const [formData, setFormData] = useState(() => parseScheduleFromApi(null).formData);
    const [scheduleReport, setScheduleReport] = useState(
      () => parseScheduleFromApi(null).scheduleReport
    );
    const [recipients, setRecipients] = useState([]);
    const [scheduleNameError, setScheduleNameError] = useState("");
    const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
    const [csvDownloadError, setCsvDownloadError] = useState("");
    const [runInfo, setRunInfo] = useState({
      next_run_at: null,
      last_run_at: null,
      last_run_status: null,
      last_run_message: "",
      run_history: [],
    });
    const loadedScheduleKeyRef = useRef(null);

    useEffect(() => {
      const scheduleKey = initialScheduleData?.id
        ? `id:${initialScheduleData.id}`
        : initialScheduleData
          ? `new:${initialScheduleData.updatedAt || "loaded"}`
          : "empty";

      if (loadedScheduleKeyRef.current === scheduleKey) {
        return;
      }

      loadedScheduleKeyRef.current = scheduleKey;
      const parsed = parseScheduleFromApi(initialScheduleData);
      setScheduleId(parsed.id);
      setFormData(parsed.formData);
      setScheduleReport(parsed.scheduleReport);
      setRecipients(parsed.recipients);
      setRunInfo(parsed.runInfo || {
        next_run_at: null,
        last_run_at: null,
        last_run_status: null,
        last_run_message: "",
        run_history: [],
      });
    }, [initialScheduleData]);

    const ranToday = useMemo(
      () => scheduleRanToday(runInfo.run_history, scheduleReport.timezone),
      [runInfo.run_history, scheduleReport.timezone]
    );

    const statusOptions = [
      { value: "active", label: t("reports.schedule.statusOptions.active") },
      { value: "inactive", label: t("reports.schedule.statusOptions.inactive") },
    ];

    const fieldDelimiterOptions = [
      { value: ",", label: "," },
      { value: ";", label: ";" },
      { value: "|", label: "|" },
      { value: "\t", label: t("reports.schedule.fieldDelimiterOptions.tab") },
    ];

  const textQualifierOptions = [
    { value: "none", label: t("reports.schedule.textQualifierOptions.none") },
    { value: "'", label: "'" },
    { value: '"', label: '"' },
  ];

    const handleInputChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
      if (name === "schedule_name" && scheduleNameError) {
        setScheduleNameError("");
      }
    };

    const handleSelectChange = (name, option) => {
      setFormData((prev) => ({
        ...prev,
        [name]: option?.value ?? "",
      }));
    };

    const findOption = (options, value) => {
      if (value === null || value === undefined || value === "") return null;
      return options.find((opt) => String(opt.value) === String(value)) || null;
    };

    const validateScheduleName = () => {
      if (!formData.schedule_name?.trim()) {
        setScheduleNameError(t("reports.schedule.scheduleNameRequired"));
        return false;
      }
      setScheduleNameError("");
      return true;
    };

    const buildPayload = () =>
      buildSchedulePayload({
        formData,
        scheduleReport,
        recipients,
        scheduleFilters: filters,
        lockFilters,
        id: scheduleId,
      });

    const handleSave = async (options = {}) => {
      if (!validateScheduleName()) {
        return false;
      }

      await updateReportMutation.mutateAsync({
        id,
        data: {
          report_schedule: buildPayload(),
          permissons: permissions,
        },
        hideNotification: options.hideNotification,
      });
      return true;
    };

    const formatRunDate = (value) =>
      value ? moment(value).format("YYYY-MM-DD HH:mm") : t("reports.schedule.notScheduled");

    const handleDownloadCsv = async () => {
      if (!id || isDownloadingCsv) return;

      setCsvDownloadError("");
      setIsDownloadingCsv(true);

      try {
        const exportColumns = Array.isArray(columns) ? columns.filter((col) => !!col?.header) : [];
        if (exportColumns.length === 0) {
          throw new Error(t("reports.schedule.downloadCsvNoColumns"));
        }

        const activeFilters = (filters || [])
          .filter((filter) => filter.is_active !== false)
          .map((filter) => ({
            ...filter,
            is_active: true,
          }));

        const blob = await downloadScheduleReportCsv(id, {
          columns: exportColumns,
          filters: activeFilters,
        });

        if (!blob || blob.size === 0) {
          throw new Error(t("reports.schedule.downloadCsvEmpty"));
        }

        const baseName =
          formData.file_name?.trim() ||
          formData.schedule_name?.trim() ||
          "schedule-export";
        const safeName = baseName.replace(/[^\w.-]+/g, "_");
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `${safeName}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        setCsvDownloadError(error?.message || t("reports.schedule.downloadCsvFailed"));
      } finally {
        setIsDownloadingCsv(false);
      }
    };

    useImperativeHandle(ref, () => ({
      handleSave,
      getSchedulePayload: buildPayload,
      validateScheduleName,
    }));

    const renderSelect = (name, options, placeholder, selectProps = {}) => (
      <Select
        activeColor={activeColor}
        styles={selectStyles}
        className="react-select"
        placeholder={placeholder}
        name={name}
        isSearchable={false}
        closeMenuOnSelect
        value={findOption(options, formData[name])}
        onChange={(option) => handleSelectChange(name, option)}
        options={options}
        {...selectProps}
      />
    );

    return (
      <Card className={scopeClassName}>
        <style>{`${getReportScheduleThemeCss(activeColorStyle)}${theme2FieldsCss}`}</style>
        <CardBody>
          <div className={isTheme2 ? "report-schedule-fields" : undefined}>
            <div className="report-schedule-panel-box" style={exportPanelStyle}>
          <h6
            className="mb-3 font-weight-bold report-schedule-panel-title"
            style={{ color: colors.titleColor }}
          >
            {t("reports.schedule.exportFtpTitle")}
          </h6>

            <Row className="mb-2 align-items-start">
              <Col md="6">
                <ScheduleFieldBox
                  label={
                    <>
                      {t("reports.schedule.scheduleName")} <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  <Input
                    name="schedule_name"
                    type="text"
                    className={`form-control ${scheduleNameError ? "is-invalid" : ""}`}
                    style={inputStyle}
                    value={formData.schedule_name}
                    onChange={handleInputChange}
                  />
                  {scheduleNameError && (
                    <div className="invalid-feedback d-block">{scheduleNameError}</div>
                  )}
                </ScheduleFieldBox>
              </Col>
              <Col md="3">
                <ScheduleFieldBox
                  label={t("reports.schedule.status")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  {renderSelect("schedule_status", statusOptions, t("reports.schedule.selectStatus"))}
                </ScheduleFieldBox>
              </Col>
            </Row>

            <Row>
              <Col md="6">
                <ScheduleFieldBox
                  label={t("reports.schedule.status")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  {renderSelect("export_status", statusOptions, t("reports.schedule.selectStatus"))}
                </ScheduleFieldBox>

                <ScheduleFieldBox
                  label={t("reports.schedule.exportDescription")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  <Input
                    name="export_description"
                    type="text"
                    className="form-control"
                    style={inputStyle}
                    value={formData.export_description}
                    onChange={handleInputChange}
                  />
                </ScheduleFieldBox>

                <ScheduleFieldBox
                  label={t("reports.schedule.fileName")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  <Input
                    name="file_name"
                    type="text"
                    className="form-control"
                    style={inputStyle}
                    value={formData.file_name}
                    onChange={handleInputChange}
                  />
                </ScheduleFieldBox>

                <ScheduleFieldBox
                  label={t("reports.schedule.vendor")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  {renderSelect("vendor", [], t("reports.schedule.selectVendor"), {
                    isDisabled: true,
                  })}
                </ScheduleFieldBox>

                <ScheduleFieldBox
                  label={t("reports.schedule.includeHeaderRow")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  <div className="d-flex align-items-center" style={{ minHeight: 38 }}>
                    <input
                      type="checkbox"
                      id="include_header_row"
                      name="include_header_row"
                      className="report-schedule-enum-checkbox"
                      checked={!!formData.include_header_row}
                      onChange={handleInputChange}
                    />
                  </div>
                </ScheduleFieldBox>

                <ScheduleFieldBox
                  label={t("reports.schedule.fieldDelimiter")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  {renderSelect("field_delimiter", fieldDelimiterOptions, t("reports.schedule.selectDelimiter"))}
                </ScheduleFieldBox>

                <ScheduleFieldBox
                  label={t("reports.schedule.textQualifier")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  {renderSelect("text_qualifier", textQualifierOptions, t("reports.schedule.selectQualifier"))}
                </ScheduleFieldBox>

                <ScheduleFieldBox
                  label={t("reports.schedule.destinationDescription")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  <Input
                    name="destination_description"
                    type="text"
                    className="form-control"
                    style={inputStyle}
                    value={formData.destination_description}
                    onChange={handleInputChange}
                  />
                </ScheduleFieldBox>
              </Col>

              <Col md="6">
                <ScheduleFieldBox
                  label={t("reports.schedule.protocol")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  <Input
                    name="protocol"
                    type="text"
                    className="form-control"
                    style={inputStyle}
                    value={formData.protocol}
                    disabled
                    readOnly
                  />
                </ScheduleFieldBox>

                <ScheduleFieldBox
                  label={t("reports.schedule.host")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  <Input
                    name="host"
                    type="text"
                    className="form-control"
                    style={inputStyle}
                    value={formData.host}
                    onChange={handleInputChange}
                  />
                </ScheduleFieldBox>

                <ScheduleFieldBox
                  label={t("reports.schedule.port")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  <Input
                    name="port"
                    type="text"
                    className="form-control"
                    style={inputStyle}
                    value={formData.port}
                    onChange={handleInputChange}
                  />
                </ScheduleFieldBox>

                <ScheduleFieldBox
                  label={t("reports.schedule.user")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  <Input
                    name="user"
                    type="text"
                    className="form-control"
                    style={inputStyle}
                    value={formData.user}
                    onChange={handleInputChange}
                  />
                </ScheduleFieldBox>

                <ScheduleFieldBox
                  label={t("reports.schedule.password")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  <div style={{ position: "relative" }}>
                    <Input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      className="form-control report-schedule-password-input"
                      style={inputStyle}
                      value={formData.password}
                      onChange={handleInputChange}
                      autoComplete="new-password"
                    />
                    <span
                      onClick={() => setShowPassword((prev) => !prev)}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: colors.iconMuted,
                        zIndex: 10,
                      }}
                    >
                      {showPassword ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
                    </span>
                  </div>
                </ScheduleFieldBox>

                <ScheduleFieldBox
                  label={t("reports.schedule.directory")}
                  fieldBoxStyle={fieldBoxStyle}
                  bodyColor={colors.bodyColor}
                >
                  <Input
                    name="directory"
                    type="text"
                    className="form-control"
                    style={inputStyle}
                    value={formData.directory}
                    onChange={handleInputChange}
                  />
                </ScheduleFieldBox>
              </Col>
            </Row>
            </div>

            {!lockFilters && (
            <ReportScheduleFilterParameters
              activeColor={activeColor}
              isTheme2={isTheme2}
              id={id}
              filters={filters}
              onFiltersChange={onFiltersChange}
              colors={colors}
              selectStyles={selectStyles}
              inputStyle={inputStyle}
            />
            )}

            <ReportScheduleReportSection
              activeColor={activeColor}
              colors={colors}
              selectStyles={selectStyles}
              inputStyle={inputStyle}
              value={scheduleReport}
              onChange={setScheduleReport}
            />

            <div className="report-schedule-recipients-history-row">
              <div className="report-schedule-recipients-history-col">
                <ReportScheduleRecipients
                  colors={colors}
                  inputStyle={inputStyle}
                  value={recipients}
                  onChange={setRecipients}
                  fillHeight
                />
              </div>
              <div className="report-schedule-recipients-history-col">
                <ReportScheduleHistoryCalendar
                  reportId={id}
                  scheduleId={scheduleId}
                  runHistory={runInfo.run_history}
                  nextRunAt={runInfo.next_run_at}
                  reportName={formData.export_description || (id ? `Report #${id}` : "")}
                  scheduleName={formData.schedule_name}
                  timezone={scheduleReport.timezone}
                  colors={colors}
                  activeColorStyle={activeColorStyle}
                  onDownloadCsv={handleDownloadCsv}
                  isDownloadingCsv={isDownloadingCsv}
                  downloadCsvDisabled={!id}
                  csvDownloadError={csvDownloadError}
                  fillHeight
                />
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }
);

ReportSchedule.displayName = "ReportSchedule";

export default ReportSchedule;
